import React, { FunctionComponent, ComponentType } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Flex, Box } from '@rebass/emotion';
import { parse } from 'querystring';

import {
  getLocaleFromPathname,
  getIntlValidLocale,
} from '../../../helpers/locale';
import Countdown from '../../CountDown';
import { CategoryBannerQuery as CategoryBannerQueryType } from './__generated__/CategoryBannerQuery';
import CategoryBannerQuery from './CategoryBanner.query';
import {
  CategoryBannerTheme,
  CategoryBannerTextAlignment,
} from './CategoryBanner.constants';
import * as styles from './CategoryBanner.styles';

const CategoryBanner: FunctionComponent<{
  data: CategoryBannerQueryType | undefined;
}> = ({ data }) => {
  const banner = data?.categoryBanner;

  if (!banner) return null;

  const {
    theme,
    text_alignment,
    body,
    countdown_date,
    countdown_template,
    background_mobile,
    background_desktop,
  } = banner;

  return (
    <Flex
      css={styles.containerStyles({
        backgroundMobile: background_mobile || '',
        backgroundDesktop: background_desktop || '',
      })}
    >
      <Box
        css={styles.contentContainerStyles({
          bannerTheme: isValidTheme(theme) ? theme : CategoryBannerTheme.Light,
          textAlignment: isValidTextAlignment(text_alignment)
            ? text_alignment
            : CategoryBannerTextAlignment.Center,
        })}
      >
        <Box dangerouslySetInnerHTML={{ __html: body || '' }} />
        {countdown_date && countdown_template ? (
          <Box
            css={styles.countdownContainerStyles}
            data-testid="category-banner-countdown-container"
          >
            <Countdown
              key="countdown"
              date={countdown_date}
              template={countdown_template}
            />
          </Box>
        ) : null}
      </Box>
    </Flex>
  );
};

export interface CategoryBannerProps {
  category: string;
}

export default compose(
  withRouter,
  graphql<CategoryBannerProps & RouteComponentProps>(CategoryBannerQuery, {
    options: ({ category, location }) => {
      const { previewToken } = parse(location.search?.substring(1));
      const language =
        location && location.pathname
          ? getIntlValidLocale(getLocaleFromPathname(location.pathname))
          : 'en';

      return { variables: { category, language, previewToken } };
    },
  })
)(CategoryBanner) as ComponentType<CategoryBannerProps>;

const isValidTheme = (theme: unknown): theme is CategoryBannerTheme =>
  Object.values(CategoryBannerTheme).includes(theme as CategoryBannerTheme);

const isValidTextAlignment = (
  textAlignment: unknown
): textAlignment is CategoryBannerTextAlignment =>
  Object.values(CategoryBannerTextAlignment).includes(
    textAlignment as CategoryBannerTextAlignment
  );
